import React, { useEffect, useRef, useState } from "react";
import { Box, LinearProgress, Typography } from "@mui/material";
import { CW, Pill } from "./cwUi";

/**
 * P4.4 — voice note / video for feedback: record in the browser
 * (MediaRecorder) OR choose a file. The page uploads it to S3 through
 * onUpload(file, { source, durationSec }).
 * Recording stops by itself at MAX_SECONDS (keeps files small).
 */

const MAX_SECONDS = 180;

const pickMime = (kind) => {
  const options =
    kind === "video"
      ? ["video/webm;codecs=vp9,opus", "video/webm;codecs=vp8,opus", "video/webm", "video/mp4"]
      : ["audio/webm;codecs=opus", "audio/webm", "audio/mp4", "audio/ogg"];
  if (typeof window === "undefined" || !window.MediaRecorder) return "";
  return options.find((m) => window.MediaRecorder.isTypeSupported && window.MediaRecorder.isTypeSupported(m)) || "";
};

const clock = (s) => `${Math.floor(s / 60)}:${String(Math.floor(s % 60)).padStart(2, "0")}`;

export const canRecord = () =>
  typeof window !== "undefined" && Boolean(window.MediaRecorder && navigator.mediaDevices && navigator.mediaDevices.getUserMedia);

export default function MediaRecorderPanel({ kind, onUpload, onClose }) {
  const [phase, setPhase] = useState("idle"); // idle | starting | recording | review | uploading | error
  const [seconds, setSeconds] = useState(0);
  const [blob, setBlob] = useState(null);
  const [blobUrl, setBlobUrl] = useState("");
  const [error, setError] = useState("");
  const [progress, setProgress] = useState(0);
  const streamRef = useRef(null);
  const recorderRef = useRef(null);
  const chunksRef = useRef([]);
  const timerRef = useRef(null);
  const liveRef = useRef(null);
  const startedRef = useRef(0);

  const stopStream = () => {
    if (streamRef.current) streamRef.current.getTracks().forEach((t) => t.stop());
    streamRef.current = null;
  };

  useEffect(
    () => () => {
      if (timerRef.current) clearInterval(timerRef.current);
      try {
        if (recorderRef.current && recorderRef.current.state !== "inactive") recorderRef.current.stop();
      } catch (_err) {
        /* ignore */
      }
      stopStream();
    },
    []
  );

  useEffect(
    () => () => {
      if (blobUrl) URL.revokeObjectURL(blobUrl);
    },
    [blobUrl]
  );

  const start = async () => {
    setError("");
    setPhase("starting");
    try {
      const stream = await navigator.mediaDevices.getUserMedia(
        kind === "video" ? { audio: true, video: { width: { ideal: 1280 }, height: { ideal: 720 } } } : { audio: true }
      );
      streamRef.current = stream;
      if (kind === "video" && liveRef.current) {
        liveRef.current.srcObject = stream;
        liveRef.current.muted = true;
        liveRef.current.play().catch(() => {});
      }
      const mimeType = pickMime(kind);
      const recorder = new window.MediaRecorder(stream, mimeType ? { mimeType, videoBitsPerSecond: 1500000 } : undefined);
      chunksRef.current = [];
      recorder.ondataavailable = (e) => e.data && e.data.size && chunksRef.current.push(e.data);
      recorder.onstop = () => {
        const type = recorder.mimeType || mimeType || (kind === "video" ? "video/webm" : "audio/webm");
        const b = new Blob(chunksRef.current, { type });
        setBlob(b);
        setBlobUrl(URL.createObjectURL(b));
        setPhase("review");
        stopStream();
      };
      recorderRef.current = recorder;
      recorder.start(1000);
      startedRef.current = Date.now();
      setSeconds(0);
      setPhase("recording");
      timerRef.current = setInterval(() => {
        const s = (Date.now() - startedRef.current) / 1000;
        setSeconds(s);
        if (s >= MAX_SECONDS) stop();
      }, 250);
    } catch (err) {
      stopStream();
      setPhase("error");
      setError(
        err && err.name === "NotAllowedError"
          ? `Allow ${kind === "video" ? "camera and microphone" : "microphone"} access in the browser, then try again.`
          : `Could not start the ${kind === "video" ? "camera" : "microphone"}.`
      );
    }
  };

  function stop() {
    if (timerRef.current) clearInterval(timerRef.current);
    timerRef.current = null;
    try {
      if (recorderRef.current && recorderRef.current.state !== "inactive") recorderRef.current.stop();
    } catch (_err) {
      /* ignore */
    }
  }

  const redo = () => {
    setBlob(null);
    setBlobUrl("");
    setSeconds(0);
    setPhase("idle");
  };

  const use = async () => {
    if (!blob) return;
    setPhase("uploading");
    setProgress(0);
    const ext = String(blob.type).includes("mp4") ? (kind === "video" ? "mp4" : "m4a") : "webm";
    const file = new File([blob], `${kind === "video" ? "video" : "voice-note"}-${Date.now()}.${ext}`, { type: blob.type });
    const ok = await onUpload(file, { source: "recorded", durationSec: Math.round(seconds), onProgress: setProgress });
    if (ok) onClose();
    else setPhase("review");
  };

  const label = kind === "video" ? "Video" : "Voice note";

  return (
    <Box sx={{ border: `1px solid ${CW.line}`, borderRadius: "12px", p: 1.5, bgcolor: CW.soft }} data-testid={`recorder-${kind}`}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
        <Typography sx={{ fontWeight: 700, fontSize: 14, color: CW.ink, flex: 1 }}>Record {label.toLowerCase()}</Typography>
        {phase === "recording" ? <Pill tone="red">● {clock(seconds)} / {clock(MAX_SECONDS)}</Pill> : null}
      </Box>

      {kind === "video" && (phase === "starting" || phase === "recording") ? (
        <video ref={liveRef} playsInline muted style={{ width: "100%", maxHeight: 280, borderRadius: 10, background: "#000" }} />
      ) : null}
      {kind === "video" && phase !== "starting" && phase !== "recording" ? <video ref={liveRef} style={{ display: "none" }} /> : null}

      {phase === "review" || phase === "uploading" ? (
        kind === "video" ? (
          <video src={blobUrl} controls playsInline style={{ width: "100%", maxHeight: 280, borderRadius: 10, background: "#000" }} />
        ) : (
          <audio src={blobUrl} controls style={{ width: "100%" }} />
        )
      ) : null}

      {phase === "uploading" ? (
        <Box sx={{ mt: 1 }}>
          <LinearProgress variant={progress ? "determinate" : "indeterminate"} value={progress} />
          <Typography sx={{ fontSize: 12, color: CW.muted, mt: 0.5 }}>Uploading… {progress ? `${progress}%` : ""}</Typography>
        </Box>
      ) : null}

      {error ? <Typography sx={{ fontSize: 13, color: CW.red, my: 1 }}>{error}</Typography> : null}

      <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", mt: 1 }}>
        {phase === "idle" || phase === "error" ? (
          <button type="button" className="small-contained-button" onClick={start}>
            Start recording
          </button>
        ) : null}
        {phase === "recording" ? (
          <button type="button" className="small-contained-button" onClick={stop}>
            Stop
          </button>
        ) : null}
        {phase === "review" ? (
          <>
            <button type="button" className="small-contained-button" onClick={use}>
              Add to feedback
            </button>
            <button type="button" className="small-contained-outlined-button" onClick={redo}>
              Record again
            </button>
          </>
        ) : null}
        {phase !== "uploading" && phase !== "recording" ? (
          <button type="button" className="small-contained-outlined-button" onClick={onClose}>
            Cancel
          </button>
        ) : null}
      </Box>
      {phase === "idle" ? (
        <Typography sx={{ fontSize: 12, color: CW.muted, mt: 1 }}>Up to {MAX_SECONDS / 60} minutes. You can listen / watch before adding it.</Typography>
      ) : null}
    </Box>
  );
}
